import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import LoginInput from "../../components/Form/LoginInput";
import edit from "../../assets/Icons/fluent_edit.svg";
import avatar from "../../assets/Icons/user-avatar.png";
import { useThunk } from "../../hooks/useThunk";
import showToast from "../../utils/toastNotifications";
import useForm from "../../hooks/useForm";
import { USER_INPUT_FIELD_TYPES } from "../../constants/userInputFieldNames";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";
import { useSelector } from "react-redux";
import { getUserProfile, updateUserProfile } from "../../store";
import { ClipLoader } from "react-spinners";
import { api } from "../../config/api";
import { useLogOut } from "../../hooks/useLogOut";

const MAX_PROFILE_IMAGE_SIZE = 5 * 1024 * 1024;
const PROFILE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const fallbackValue = (value) => value || "Not provided";

const getInitials = (firstName, lastName) => {
  const initials = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.trim();
  return initials || "CP";
};

const getSignInMethod = (providers = []) => {
  const hasEmail = providers.includes("local");
  const hasGoogle = providers.includes("google");
  if (hasEmail && hasGoogle) return "Email and Google";
  if (hasGoogle) return "Google";
  return "Email";
};

const formatWriterRating = (rating) => {
  const numericRating = Number(rating);
  return Number.isFinite(numericRating) && numericRating >= 1 && numericRating <= 5
    ? `${numericRating.toFixed(1)}/5`
    : "Not rated";
};

const Profile = () => {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.auth.user) || {};
  const currentProfilePicture = userData.profilePicture;

  const [uploadedImage, setUploadedImage] = useState(null); // State for uploaded image
  const [isMissingOrMalformed, setIsMissingOrMalformed] = useState(false);
  const [preferredWriters, setPreferredWriters] = useState([]);
  const [isLoadingPreferredWriters, setIsLoadingPreferredWriters] = useState(true);

  const fileInputRef = useRef(null); // Ref for the hidden file input

  // defining thunks
  const [doUpdateUserProfile, isUpdatingUserProfile] = useThunk(updateUserProfile);
  const [doGetUserProfile] = useThunk(getUserProfile);
  const [doUploadImage, isUploadingImage] = useFirebaseFileUpload();
  const [logOut] = useLogOut();

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setFormData,
  } = useForm(
    {
      firstName: userData?.firstName,
      lastName: userData?.lastName,
      phoneNumber: userData?.phoneNumber,
    },
    {
      firstName: true,
      lastName: true,
      phoneNumber: false,
    },
    {
      firstName: USER_INPUT_FIELD_TYPES.FIRST_NAME,
      lastName: USER_INPUT_FIELD_TYPES.LAST_NAME,
      phoneNumber: USER_INPUT_FIELD_TYPES.PHONE,
    }
  );

  const fullName = `${formData.firstName || ""} ${formData.lastName || ""}`.trim();
  const initials = getInitials(formData.firstName, formData.lastName);
  const imagePreview = uploadedImage ? URL.createObjectURL(uploadedImage) : currentProfilePicture;
  const clientId = userData?.userId || userData?.customerId;
  const completionFields = [
    formData.firstName,
    formData.lastName,
    formData.phoneNumber,
    userData.email,
    currentProfilePicture || uploadedImage,
  ];
  const completion = Math.round((completionFields.filter(Boolean).length / completionFields.length) * 100);

  useEffect(() => {
    if (!clientId) {
      doGetUserProfile();
    }
  }, [clientId, doGetUserProfile]);

  useEffect(() => {
    let isMounted = true;
    api.get("/user/preferred-writers")
      .then((response) => {
        if (isMounted) setPreferredWriters(response.data?.data?.preferredWriters || []);
      })
      .catch(() => {
        if (isMounted) setPreferredWriters([]);
      })
      .finally(() => {
        if (isMounted) setIsLoadingPreferredWriters(false);
      });
    return () => { isMounted = false; };
  }, []);

  // Trigger click on the hidden file input
  const handleEditImageClick = () => {
    fileInputRef.current.click();
  };

  // sync uploading images with state
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!PROFILE_IMAGE_TYPES.includes(file.type)) {
        showToast("error", "Please select a JPG, PNG, or WebP image.");
        e.target.value = "";
        return;
      }
      if (file.size > MAX_PROFILE_IMAGE_SIZE) {
        showToast("error", "Profile pictures must be 5 MB or smaller.");
        e.target.value = "";
        return;
      }
      setUploadedImage(file);
    }
  };

  // cancel the operation, clear data and back
  const handleCancelClick = () => {
    setFormData({
      firstName: "",
      lastName: "",
      phoneNumber: "",
    });
    setUploadedImage(null);
    setIsMissingOrMalformed(false);
    navigate(-1);
  };

  // handle the save button
  const handleSaveClick = async () => {
    if (validateForm()) {
      if (
        !uploadedImage &&
        formData.firstName === userData?.firstName &&
        formData.lastName === userData?.lastName &&
        formData.phoneNumber === userData?.phoneNumber
      ) {
        showToast("info", "You haven't made any changes.");
        return;
      }

      let updatedFormData = { ...formData };

      if (uploadedImage) {
        const uploadResult = await doUploadImage(
          [uploadedImage],
          "user/profilePicture"
        );
        if (!uploadResult.success) {
          showToast("error", uploadResult?.error?.message || "Failed to upload files");
          return;
        }
        updatedFormData.profilePicture = uploadResult?.uploadedUrls[0];
      }

      const result = await doUpdateUserProfile(updatedFormData);

      if (result.success) {
        showToast("success", result.response?.message);
        handleCancelClick();
      } else {
        showToast("error", result.error?.message);
      }
    } else {
      showToast("error", "Please Provide Required and Valid Information");
    }
  };

  return (
    <div className="mt-24 flex w-full flex-col px-4 xl:w-[calc(100vw-256px)] xl:px-8">
      <div className="mb-8 max-w-[1080px]">
        <div className="overflow-hidden rounded-2xl border border-[#DDE6F1] bg-white shadow-[0_16px_40px_rgba(15,23,42,0.06)]">
          <div className="bg-gradient-to-r from-[#102A43] via-[#1E4A7A] to-[#2E6B4F] px-5 py-6 sm:px-7">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="relative h-[112px] w-[112px] shrink-0 overflow-hidden rounded-2xl border border-white/30 bg-white/10 shadow-lg">
                  {isMissingOrMalformed && !uploadedImage ? (
                    <div className="flex h-full w-full items-center justify-center bg-[#EAF3F7] font-roboto text-[30px] font-bold text-[#1E4A7A]">
                      {initials}
                    </div>
                  ) : (
                    <img
                      src={imagePreview || avatar}
                      alt="Profile"
                      onError={() => setIsMissingOrMalformed(true)}
                      className="h-full w-full object-cover"
                    />
                  )}
                  <button
                    type="button"
                    className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-primary shadow-md"
                    onClick={handleEditImageClick}
                    aria-label="Change profile picture"
                  >
                    <img src={edit} alt="" className="h-4 w-4" />
                  </button>
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-roboto text-[28px] font-bold leading-tight text-white">
                      {fallbackValue(fullName)}
                    </h2>
                    <span className="max-w-full break-all rounded-full bg-white/10 px-3 py-1 font-inter text-[12px] font-bold text-white ring-1 ring-white/20">
                      Client ID: {fallbackValue(clientId)}
                    </span>
                  </div>
                  <p className="mt-2 font-inter text-[14px] text-white/75">
                    {fallbackValue(userData.email)}
                  </p>
                  <p className="mt-3 max-w-[520px] font-inter text-[13px] leading-5 text-white/60">
                    Keep your contact details current so our support team can reach you quickly about active orders.
                  </p>
                  <p className="mt-1 font-inter text-[12px] text-white/60">
                    Profile picture: JPG, PNG, or WebP. Maximum 5 MB.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/20 md:min-w-[230px]">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-inter text-[12px] font-semibold uppercase tracking-[0.1em] text-white/70">
                    Profile Complete
                  </span>
                  <span className="font-roboto text-[20px] font-bold text-white">
                    {completion}%
                  </span>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/20">
                  <div className="h-full rounded-full bg-white" style={{ width: `${completion}%` }} />
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 bg-[#F8FAFC] p-5 sm:p-7 lg:grid-cols-[1fr_280px]">
            <section>
              <div className="mb-4">
                <h3 className="font-roboto text-[18px] font-bold text-[#162033]">
                  Personal Details
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <LoginInput
                  className="w-full px-4"
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  type="text"
                  value={formData.firstName}
                  placeholder="First Name"
                  error={touched.firstName && errors.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  onBlur={() => handleBlur("firstName", formData.firstName)}
                  isRequired={true}
                  isFloatingLabel={false}
                />
                <LoginInput
                  className="w-full px-4"
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  type="text"
                  value={formData.lastName}
                  placeholder="Last Name"
                  error={touched.lastName && errors.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  onBlur={() => handleBlur("lastName", formData.lastName)}
                  isRequired={true}
                  isFloatingLabel={false}
                />
                <LoginInput
                  className="w-full px-4"
                  id="phoneNumber"
                  name="phoneNumber"
                  label="Phone Number"
                  type="text"
                  value={formData.phoneNumber}
                  placeholder="Contact Number"
                  error={touched.phoneNumber && errors.phoneNumber}
                  onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                  onBlur={() => handleBlur("phoneNumber", formData.phoneNumber)}
                  isRequired={false}
                  isFloatingLabel={false}
                />
                <LoginInput
                  className="w-full px-4"
                  inputStyle="bg-neutral-200"
                  id="email"
                  name="email"
                  label="Email"
                  type="text"
                  value={userData.email}
                  disabled={true}
                  isFloatingLabel={false}
                />
              </div>

              <div className="mt-7 rounded-2xl border border-[#E7ECF3] bg-white p-5">
                <h3 className="font-roboto text-[18px] font-bold text-[#162033]">Account Information</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {[
                    ["Customer ID", fallbackValue(clientId)],
                    ["Registered email", fallbackValue(userData.email)],
                    ["Member since", userData.createdAt ? new Date(userData.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "Not available"],
                    ["Sign-in method", getSignInMethod(userData.providers)],
                    ["Local timezone", userData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"],
                  ].map(([label, value]) => (
                    <div key={label} className="min-w-0 rounded-xl bg-[#F8FAFC] px-4 py-3">
                      <p className="font-inter text-[12px] font-semibold uppercase tracking-wide text-[#667085]">{label}</p>
                      <p className="mt-1 break-words font-inter text-[14px] font-semibold text-[#263247]">{value}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 font-inter text-[12px] leading-5 text-[#667085]">
                  Dates and times in your dashboard and emails are shown in this timezone.
                </p>
              </div>

              <div className="mt-7 rounded-2xl border border-[#E7ECF3] bg-white p-5">
                <h3 className="font-roboto text-[18px] font-bold text-[#162033]">Preferred Writers</h3>
                <p className="mt-1 font-inter text-[13px] leading-5 text-[#667085]">
                  Writers you specifically requested and who worked on your orders.
                </p>
                <div className="mt-4 overflow-hidden rounded-xl border border-[#E7ECF3]">
                  {isLoadingPreferredWriters ? (
                    <div className="flex min-h-[96px] items-center justify-center"><ClipLoader size={22} color="#0020BD" /></div>
                  ) : preferredWriters.length === 0 ? (
                    <p className="px-4 py-6 text-center font-inter text-[13px] text-[#667085]">
                      No preferred writers yet. Writers you specifically request and work with will appear here.
                    </p>
                  ) : preferredWriters.map((writer, index) => (
                    <div key={writer.writerId} className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between ${index > 0 ? "border-t border-[#E7ECF3]" : ""}`}>
                      <div className="flex min-w-0 items-center gap-3">
                        {writer.profilePicture ? (
                          <img src={writer.profilePicture} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EAF3F7] font-bold text-primary">{getInitials(writer.firstName, writer.lastName)}</div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-inter text-[14px] font-bold text-[#263247]">{[writer.title, writer.firstName, writer.lastName].filter(Boolean).join(" ")}</p>
                          <p className="font-inter text-[12px] text-[#667085]">{writer.writerId} · {formatWriterRating(writer.overallRating)} · {writer.orderCount} {writer.orderCount === 1 ? "order" : "orders"} together</p>
                          {!writer.isAvailable && <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Unavailable</span>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 sm:justify-end">
                        {writer.orders.map((order) => (
                          <button key={order.orderId} type="button" onClick={() => navigate(`/thread?roomIdParam=${encodeURIComponent(order.orderId)}&threadIdParam=${order.threadId}`)} className="rounded-lg border border-[#CBD5E1] bg-white px-3 py-1.5 font-inter text-[12px] font-semibold text-primary hover:bg-[#F8FAFC]">
                            {order.orderId}
                          </button>
                        ))}
                        {writer.orderCount > writer.orders.length && (
                          <span className="inline-flex items-center px-1.5 font-inter text-[12px] font-semibold text-[#667085]">
                            +{writer.orderCount - writer.orders.length} more
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <aside className="space-y-4">
              <div className="rounded-2xl border border-[#E7ECF3] bg-white p-5 shadow-[0_8px_20px_rgba(21,34,50,0.04)]">
                <h3 className="font-roboto text-[18px] font-bold text-[#162033]">
                  Account Actions
                </h3>
                <p className="mt-1 font-inter text-[13px] leading-5 text-[#667085]">
                  Save your profile changes, return to the previous page, or sign out.
                </p>

                <div className="mt-5 grid gap-3">
                  <button
                    type="button"
                    className="min-h-[44px] rounded-lg border border-[#CBD5E1] bg-white px-4 py-2 font-roboto text-[14px] font-bold text-[#475569] transition hover:bg-[#F8FAFC]"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-4 py-2 font-roboto text-[14px] font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={handleSaveClick}
                    disabled={isUploadingImage || isUpdatingUserProfile}
                  >
                    {isUploadingImage || isUpdatingUserProfile ? (
                      <ClipLoader
                        loading={isUpdatingUserProfile || isUploadingImage}
                        speedMultiplier={1}
                        size={22}
                        color="#ffffff"
                      />
                    ) : (
                      "Save Profile"
                    )}
                  </button>
                  <div className="border-t border-[#E7ECF3] pt-3">
                    <button
                      type="button"
                      className="flex min-h-[44px] w-full items-center justify-center rounded-lg border border-[#DC2626] bg-white px-4 py-2 font-roboto text-[14px] font-bold text-[#B91C1C] transition hover:bg-[#FEF2F2]"
                      onClick={logOut}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        ref={fileInputRef}
        className="hidden"
        onChange={handleImageChange}
      />
    </div>
  );
};

export default Profile;
